// frontend/src/components/homepage/SceneWashes.js
//
// The colour the page is standing in. Two fixed layers, one gradient each,
// crossfaded over 1.6s as the reader moves between sections - so the background
// warms toward rose in the heat sections and cools toward teal in the working
// ones without any hard edge ever being visible.
//
// WHY TWO LAYERS AND NOT ONE ANIMATED BACKGROUND. `background-image` is not an
// interpolable property between arbitrary gradients: transitioning it snaps.
// Two stacked layers with an opacity crossfade is the only way to get a real
// fade, and it costs one extra composited layer that is never repainted.
//
// WHICH SECTION WINS. The same `-45% 0px -45% 0px` rootMargin the mesh uses, so
// the wash and the graph change together on the same boundary: the section that
// owns the middle 10% of the viewport owns the page. A section with NO
// `data-scene` attribute is deliberately not observed and therefore KEEPS the
// scene it was entered with - which is how the pinned live-feed section holds
// one colour through its whole scroll instead of strobing.
//
// UNDER MOTION OFF (and under the prerenderer) it paints the hero wash and stops
// observing. The crossfade is a transition, so `transition: none` would turn
// every boundary into a colour jump on scroll - louder than the effect it
// replaces. A single calm background is the honest still version of this.
//
// It renders BELOW the mesh: this file is mounted before MeshCanvas and both
// sit at z-index 0, so DOM order alone decides. Content is at z-index 2.

import React, { useEffect, useRef } from "react";
import useMotion from "../../motion/useMotion";
import { isPrerendering } from "../../motion/useInViewGate";

/**
 * One soft gradient per section, verbatim from the design prototype.
 *
 * Keys are the `data-scene` values SectionShell writes. A section naming a scene
 * that is not here simply keeps the current one - a missing entry can never
 * blank the page.
 */
export const SCENES = {
  hero: "linear-gradient(150deg,#EDFAF8 0%,#F8FDFF 45%,#FDF3F7 100%)",
  clients: "linear-gradient(160deg,#F3FAFD 0%,#FFFFFF 50%,#F5FBF9 100%)",
  features: "linear-gradient(150deg,#ECFAF6 0%,#FBFEFD 48%,#FFF4F7 100%)",
  types: "linear-gradient(200deg,#FFF3F6 0%,#FFFFFF 50%,#EFFAF8 100%)",
  pricing: "linear-gradient(160deg,#F4F5FE 0%,#FBFDFF 50%,#EEFBF8 100%)",
  numbers: "linear-gradient(150deg,#E6F8F3 0%,#F4FDFA 50%,#FFF2F6 100%)",
  cta: "linear-gradient(140deg,#FFF1F5 0%,#FBFDFF 50%,#E9FAF6 100%)",
  faq: "linear-gradient(170deg,#F8FBFB 0%,#FFFFFF 55%,#F3FBF9 100%)",
  blog: "linear-gradient(150deg,#FDF3F7 0%,#F8FDFF 55%,#EDFAF8 100%)",
};

/** The scene the page opens on, and the one it falls back to. */
export const DEFAULT_SCENE = "hero";

const SECTION_ROOT_MARGIN = "-45% 0px -45% 0px";

/**
 * Put a scene's gradient on a layer.
 *
 * Through a CUSTOM PROPERTY, not `style.background`, for two reasons: the CSS
 * rule stays in the stylesheet where the rest of the layer's styling lives, and
 * a custom property is stored verbatim by every engine - the `background`
 * shorthand refuses a `linear-gradient()` value in jsdom, so the imperative
 * version could not be asserted on at all.
 */
function setWash(layer, name) {
  layer.style.setProperty("--hl-scene-bg", SCENES[name]);
}

export default function SceneWashes() {
  const aRef = useRef(null);
  const bRef = useRef(null);
  const { off } = useMotion();

  useEffect(() => {
    const a = aRef.current;
    const b = bRef.current;
    if (!a || !b) return undefined;

    // Layer A always carries the opening scene, whether or not anything is
    // allowed to change it afterwards.
    setWash(a, DEFAULT_SCENE);
    a.classList.add("hl-scene-on");
    b.classList.remove("hl-scene-on");

    if (off || isPrerendering()) return undefined;
    if (typeof IntersectionObserver === "undefined") return undefined;

    let front = a;
    let back = b;
    let current = DEFAULT_SCENE;

    const show = (name) => {
      if (!name || name === current || !SCENES[name]) return;
      current = name;
      setWash(back, name);
      back.classList.add("hl-scene-on");
      front.classList.remove("hl-scene-on");
      const swap = front;
      front = back;
      back = swap;
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) show(entry.target.dataset.scene);
        });
      },
      { rootMargin: SECTION_ROOT_MARGIN }
    );
    document.querySelectorAll("[data-scene]").forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [off]);

  // Layer A is RENDERED lit with the opening scene, exactly what the effect
  // above writes first: the prerendered snapshot is taken after that effect
  // has run, so the hydrating client's first render has to say the same.
  // Nothing here re-renders, so React never overwrites the imperative swaps.
  return (
    <div className="hl-scenes" aria-hidden="true">
      <div
        className="hl-scene hl-scene-on"
        ref={aRef}
        style={{ "--hl-scene-bg": SCENES[DEFAULT_SCENE] }}
      />
      <div className="hl-scene" ref={bRef} />
    </div>
  );
}
